import { BookOpen, Check, Clock, GraduationCap } from "lucide-react";
import { cn } from "@/utils/cn";
import { NUMBER_TONES, PrimaryButton, SecondaryButton, WizardCard } from "./visuals";

/**
 * After the agents are made: they are a draft in the Touch Builder, where the
 * firm tests them in the simulator and publishes. From a roteiro, the page
 * also shows the agents it created, in the order the client meets them. What
 * still waits for the WhatsApp is said once, with the one button for it.
 */

export default function ResultPanel({
  agentCount,
  agents = [],
  document = null,
  whatsappConnected,
  waitingCount,
  onOpenBuilder,
  onConnectWhatsapp,
  onSecondary,
  secondaryLabel,
  onTraining = null,
}) {
  return (
    <WizardCard as="section" aria-labelledby="result-title" className="mx-auto max-w-xl p-8">
      <div className="text-center">
        <span aria-hidden="true" className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
          <Check className="h-7 w-7" strokeWidth={2.5} />
        </span>
        <h2 id="result-title" className="mt-4 text-2xl font-semibold tracking-tight text-foreground">
          {agentCount === 1 ? "Agente criado" : `${agentCount} agentes criados`}
        </h2>
        <p className="mt-2 text-[15px] text-muted-foreground">Teste no simulador e publique quando estiver do seu jeito.</p>
      </div>

      {agents.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-foreground">Seu atendimento ficou assim</h3>
          <ol className="mt-2" aria-label="Agentes criados">
            {agents.map((a, i) => (
              <li key={`${a.name}-${i}`} className="flex items-center gap-3 border-b border-black/[.05] py-2.5 last:border-0 dark:border-white/[.06]">
                <span aria-hidden="true" className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold", NUMBER_TONES[i % NUMBER_TONES.length])}>
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] font-medium text-foreground">{a.name}</span>
                  {a.does && <span className="block truncate text-[13px] text-muted-foreground">{a.does}</span>}
                </span>
              </li>
            ))}
          </ol>
          {document?.indexed && (
            <p className="mt-3 flex items-center gap-2 text-[13px] text-muted-foreground">
              <BookOpen className="h-4 w-4 shrink-0" aria-hidden="true" />
              {document.name} fica na base de conhecimento para os agentes consultarem.
            </p>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <SecondaryButton onClick={onSecondary}>{secondaryLabel}</SecondaryButton>
        <PrimaryButton onClick={onOpenBuilder}>Testar no Touch Builder</PrimaryButton>
      </div>

      {!whatsappConnected && (
        <div className="mt-7 flex flex-wrap items-center gap-3 rounded-2xl border border-amber-200/70 bg-amber-50 px-4 py-3 text-left dark:border-amber-400/20 dark:bg-amber-500/10">
          <Clock className="h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm text-amber-900 dark:text-amber-100">
            {waitingCount > 0
              ? `Conecte o WhatsApp para os agentes atenderem. ${waitingCount === 1 ? "1 item liga" : `${waitingCount} itens ligam`} junto.`
              : "Conecte o WhatsApp para os agentes atenderem."}
          </p>
          <button
            type="button"
            onClick={onConnectWhatsapp}
            className="inline-flex min-h-[40px] items-center rounded-full bg-foreground px-4 text-[13px] font-semibold text-background"
          >
            Conectar WhatsApp
          </button>
        </div>
      )}

      {onTraining && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-black/[.08] px-4 py-3 text-left dark:border-white/10">
          <GraduationCap className="h-5 w-5 shrink-0 text-violet-600 dark:text-violet-300" aria-hidden="true" />
          <p className="min-w-0 flex-1 text-sm text-foreground">
            Quer ajuda para começar? <span className="text-muted-foreground">1h com um especialista, R$ 499.</span>
          </p>
          <button type="button" onClick={onTraining} className="min-h-[40px] text-sm font-semibold text-foreground underline-offset-4 hover:underline">
            Ver treinamento
          </button>
        </div>
      )}
    </WizardCard>
  );
}
